import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Home,
  Loader2,
  MessageCircle,
  MessageSquare,
  Phone,
  Smartphone,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';
import { trpc, trpcClient } from '@/utils/trpc';

export const Route = createFileRoute('/link-whatsapp')({
  component: RouteComponent,
});

function RouteComponent() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLinking, setIsLinking] = useState(false);

  const { data: session, isPending: sessionPending } = authClient.useSession();
  const navigate = Route.useNavigate();

  const whatsappStatus = useQuery(trpc.whatsapp.getStatus.queryOptions());
  const queryClient = useQueryClient();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!session && !sessionPending) {
      navigate({
        to: '/login',
      });
    }
  }, [session, sessionPending, navigate]);

  // Redirect to dashboard if already linked
  useEffect(() => {
    if (whatsappStatus.data?.linked) {
      toast.success('WhatsApp já está vinculado!', {
        description: 'Você será redirecionado para o dashboard.',
      });
      setTimeout(() => {
        navigate({ to: '/dashboard' });
      }, 2000);
    }
  }, [whatsappStatus.data?.linked, navigate]);

  const linkMutation = useMutation({
    mutationFn: (data: { phoneNumber: string }) =>
      trpcClient.whatsapp.linkPhone.mutate(data),
    onSuccess: (data) => {
      toast.success('WhatsApp vinculado com sucesso! 🎉', {
        description:
          'Agora você pode enviar mensagens para gerenciar suas finanças.',
      });
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'getStatus'] });
      setPhoneNumber('');
      setIsLinking(false);

      // Redirect to dashboard after success
      setTimeout(() => {
        navigate({ to: '/dashboard' });
      }, 3000);
    },
    onError: (error) => {
      toast.error('Falha ao vincular WhatsApp', {
        description: error.message,
      });
      setIsLinking(false);
    },
  });

  const testMutation = useMutation({
    mutationFn: (data: { message: string }) =>
      trpcClient.whatsapp.testSend.mutate(data),
    onSuccess: () => {
      toast.success('Mensagem de teste enviada!', {
        description: 'Verifique seu WhatsApp para confirmar a conexão.',
      });
    },
    onError: (error) => {
      toast.error('Falha ao enviar mensagem de teste', {
        description: error.message,
      });
    },
  });

  const handleLinkPhone = async () => {
    if (!phoneNumber) {
      toast.error('Por favor, insira um número de telefone');
      return;
    }

    setIsLinking(true);
    linkMutation.mutate({ phoneNumber });
  };

  const handleTestMessage = () => {
    testMutation.mutate({
      message:
        'Olá! Seu WhatsApp foi vinculado com sucesso ao Control Money! 🎉\n\nAgora você pode:\n• Registrar gastos: "comprei 25 de café"\n• Fazer perguntas: "quanto gastei este mês?"\n• Enviar mensagens de voz 🎤\n\nDigite /help para ver todos os comandos disponíveis!',
    });
  };

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace('whatsapp:', '').replace(/\D/g, '');
    if (cleaned.startsWith('55')) {
      const match = cleaned.match(/^(55)(\d{2})(\d{5})(\d{4})$/);
      if (match) {
        return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
      }
    }
    return phone;
  };

  if (sessionPending || whatsappStatus.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = whatsappStatus.data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <Link
            className="flex items-center text-muted-foreground text-sm transition-colors hover:text-foreground"
            to="/dashboard"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar para Dashboard
          </Link>
          <Link
            className="flex items-center text-muted-foreground text-sm transition-colors hover:text-foreground"
            to="/"
          >
            <Home className="mr-2 h-4 w-4" />
            Início
          </Link>
        </div>

        <div className="mx-auto max-w-2xl space-y-6">
          {/* Title */}
          <div className="space-y-2 text-center">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-500/10 p-3">
                <MessageCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <h1 className="font-bold text-3xl">Conectar WhatsApp</h1>
            <p className="text-muted-foreground">
              Vincule seu número do WhatsApp para gerenciar suas finanças
              através de mensagens
            </p>
          </div>

          {status?.linked ? (
            /* Already Connected */
            <Card className="border-green-500/20 bg-green-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <CheckCircle className="h-5 w-5" />
                  WhatsApp Conectado!
                </CardTitle>
                <CardDescription>
                  Seu número já está vinculado e funcionando
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-green-500/20 bg-green-500/10 p-4">
                  <div className="flex items-center space-x-3">
                    <Smartphone className="h-6 w-6 text-green-600" />
                    <div>
                      <p className="font-medium text-green-700 dark:text-green-300">
                        {formatPhoneNumber(status.phoneNumber || '')}
                      </p>
                      <p className="text-green-600 text-sm dark:text-green-400">
                        Conectado e ativo
                      </p>
                    </div>
                  </div>
                  <Badge
                    className="bg-green-500/20 text-green-700 dark:text-green-300"
                    variant="default"
                  >
                    Ativo
                  </Badge>
                </div>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button
                    className="flex-1"
                    disabled={testMutation.isPending}
                    onClick={handleTestMessage}
                  >
                    {testMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Enviar Mensagem de Boas-vindas
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={() => navigate({ to: '/settings' })}
                    variant="outline"
                  >
                    Gerenciar Configurações
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            /* Connection Form */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Vincular WhatsApp
                </CardTitle>
                <CardDescription>
                  Digite seu número do WhatsApp para começar a usar o Control
                  Money via chat
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <Label htmlFor="phone">Número do WhatsApp</Label>
                  <div className="flex space-x-2">
                    <Input
                      className="flex-1"
                      id="phone"
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleLinkPhone();
                        }
                      }}
                      placeholder="11999999999"
                      type="tel"
                      value={phoneNumber}
                    />
                    <Button
                      disabled={isLinking || linkMutation.isPending}
                      onClick={handleLinkPhone}
                      size="lg"
                    >
                      {(isLinking || linkMutation.isPending) && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      <Phone className="mr-2 h-4 w-4" />
                      Vincular
                    </Button>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    Digite apenas números (DDD + número). Exemplo: 11999999999
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* How to Use */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Como Usar
              </CardTitle>
              <CardDescription>
                Aprenda a gerenciar suas finanças através do WhatsApp
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <h4 className="font-semibold">📝 Registrar Gastos</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span>"comprei 50 reais de café"</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span>"gastei 25 com uber"</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-blue-600" />
                      <span className="font-medium text-blue-700 dark:text-blue-300">
                        🎤 Ou envie uma mensagem de voz!
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold">💬 Fazer Perguntas</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span>"quanto gastei este mês?"</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span>"como está meu orçamento?"</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span>"posso comprar algo de 500?"</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="mt-0.5 h-5 w-5 text-blue-600" />
                  <div className="text-sm">
                    <p className="mb-1 font-medium text-blue-900 dark:text-blue-100">
                      Começando
                    </p>
                    <p className="text-blue-800 dark:text-blue-200">
                      Após conectar, envie qualquer mensagem no WhatsApp. A IA
                      entende português naturalmente! Você também pode enviar
                      mensagens de voz que são automaticamente transcritas.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
